import { useEffect, useRef, useState } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import { alertFieldProps, FlashMessages, HiddenFields, HelpLine, SignInCard, SubmitButton, useBusy, type SignInPageProps } from '@/components/marketing/signin/SignInParts'

/**
 * /verify-otp — the emailed code, shown as six cells. ONE real field sits under
 * them: a single `autocomplete="one-time-code"` input is what lets iOS and
 * Android offer the code from the email, takes a pasted "123 456" whole, and
 * posts `otp` directly. The cells are drawn only once the page is interactive;
 * until then (and without JavaScript) the plain field is what shows, so nobody
 * types into boxes that cannot update. Reaching six digits submits the form.
 */
interface VerifyOtpProps extends SignInPageProps {
  /** Shown, never posted: the server holds the sign-in in progress in the
   *  session (Portal::PendingLogin), so the email is in no URL and no form. */
  email: string
  /** "Use a different email" — the hub login in the hub flow, /log-in otherwise. */
  back_path: string
}

const CODE_LENGTH = 6

export default function VerifyOtp(props: VerifyOtpProps) {
  const [code, setCode] = useState('')
  const [cells, setCells] = useState(false)
  const [focused, setFocused] = useState(false)
  const [busy, setBusy] = useBusy()
  const [resending, setResending] = useBusy()
  const formRef = useRef<HTMLFormElement>(null)
  // Set by a change that completed the code; the effect below submits once
  // React has written the FILTERED digits into the field. Submitting inside
  // the change handler would post what was typed — "123 456" from a paste —
  // because the field still holds it until the re-render.
  const submitWhenComplete = useRef(false)

  // No maxLength on the field: it would cut a pasted "123 456" or "123-456"
  // to "123 45" before this filter ever saw it. Digits only, then six.
  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    submitWhenComplete.current = digits.length === CODE_LENGTH
  }

  useEffect(() => setCells(true), [])

  useEffect(() => {
    if (!submitWhenComplete.current || busy || code.length !== CODE_LENGTH) return
    submitWhenComplete.current = false
    // requestSubmit (not submit) so the busy state below runs as for a click.
    formRef.current?.requestSubmit()
  }, [code, busy])

  return (
    <AtmosphericHero minHeight="auto" containerClassName="max-w-xl pt-8! lg:pt-10!">
      <div className="space-y-6">
        <FlashMessages {...props.flash} />
        <SignInCard
          eyebrow="Account Hub"
          title="Check your email"
          lead={<>We sent a 6-digit code to<br /><strong className="break-all text-white">{props.email}</strong></>}
        >
          <form ref={formRef} method="post" action={props.paths.verify} className="space-y-4" onSubmit={() => setBusy(true)}>
            <HiddenFields csrf={props.csrf} returnTo={props.return_to} hub={props.hub} />
            <div>
              <label htmlFor="otp" className="apply-label mb-3 block">Verification code</label>
              <div className="relative">
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  {...alertFieldProps(props.flash.alert, 'otp-hint')}
                  className={cells
                    // Over the cells, invisible but live: it takes the taps, the
                    // keyboard, the paste and the phone's code suggestion.
                    ? 'absolute inset-0 z-10 h-full w-full cursor-text border-0 bg-transparent text-base text-transparent caret-transparent outline-none selection:bg-transparent'
                    : 'apply-input text-center font-mono text-2xl tracking-[0.5em]'}
                />
                {cells && <CodeCells code={code} focused={focused} invalid={Boolean(props.flash.alert)} />}
              </div>
              <p id="otp-hint" className="mt-3 text-xs text-white/50">The code works for 10 minutes.</p>
            </div>
            <SubmitButton busy={busy} idle="Verify code" working="Verifying…" />
          </form>

          <div className="space-y-3 border-t border-white/10 pt-6 text-center text-sm text-white/60">
            <form method="post" action={props.paths.resend} onSubmit={() => setResending(true)}>
              <HiddenFields csrf={props.csrf} returnTo={props.return_to} hub={props.hub} />
              Didn&apos;t receive it?{' '}
              <button
                type="submit"
                disabled={resending}
                className="font-medium text-[#ff8c4d] underline-offset-4 hover:underline disabled:cursor-wait disabled:opacity-60"
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            </form>
            <p>
              <a href={props.back_path} className="font-medium text-[#ff8c4d] underline-offset-4 hover:underline">Use a different email</a>
            </p>
          </div>
        </SignInCard>
        <HelpLine props={props} />
      </div>
    </AtmosphericHero>
  )
}

/** The six cells the code is drawn in — display only; the field above is the input. */
function CodeCells({ code, focused, invalid }: { code: string; focused: boolean; invalid: boolean }) {
  const active = Math.min(code.length, CODE_LENGTH - 1)
  return (
    <div aria-hidden="true" className="grid grid-cols-6 gap-2 sm:gap-3">
      {Array.from({ length: CODE_LENGTH }, (_, i) => {
        const digit = code[i]
        const current = focused && i === active
        return (
          <div
            key={i}
            // Colours inline: a site rule reached the first cell and painted it
            // orange over its class.
            style={{
              borderColor: current ? '#ff6a1a' : invalid ? 'rgba(248,113,113,0.5)' : digit ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)',
              boxShadow: current ? '0 0 0 3px rgba(255,106,26,0.25)' : 'none',
            }}
            className="flex h-14 items-center justify-center rounded-xl border bg-white/[0.04] font-mono text-2xl font-semibold text-white transition-colors sm:h-16"
          >
            {digit ?? (current && <span className="h-7 w-px animate-pulse bg-white/70" />)}
          </div>
        )
      })}
    </div>
  )
}
